import React from 'react';
import { RubyLoader } from './RubyLoader';

/**
 * Shared skeleton primitives for skeleton-first page loads.
 *
 * Pages render their real layout immediately and hold each data region with
 * these placeholders instead of blanking the screen behind a spinner.
 * `dark` sits on the black/dark-900 surfaces, `light` on white cards /
 * gray-50 content areas (pos-config, terminal detail).
 */

export type SkeletonVariant = 'dark' | 'light';

const TONE: Record<SkeletonVariant, string> = {
  dark: 'bg-white/[0.08]',
  light: 'bg-gray-200',
};

export const Skeleton: React.FC<{ className?: string; variant?: SkeletonVariant }> = ({
  className = '',
  variant = 'dark',
}) => (
  <div aria-hidden="true" className={`animate-pulse rounded-md ${TONE[variant]} ${className}`} />
);

/** Stack of list-row placeholders — tables, feeds, ticket/order lists. */
export const SkeletonRows: React.FC<{
  count?: number;
  variant?: SkeletonVariant;
  className?: string;
}> = ({ count = 4, variant = 'dark', className = '' }) => (
  <div className={`relative space-y-3 ${className}`} aria-hidden="true">
    <RubyLoader variant="overlay" />
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center gap-3">
        <Skeleton variant={variant} className="h-9 w-9 rounded-lg shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton variant={variant} className="h-3 w-1/2" />
          <Skeleton variant={variant} className="h-3 w-1/3" />
        </div>
        <Skeleton variant={variant} className="h-3 w-16 shrink-0" />
      </div>
    ))}
  </div>
);

/** Grid of white card placeholders for the light content areas (hardware catalog/bundles). */
export const SkeletonCardGrid: React.FC<{ count?: number; className?: string }> = ({
  count = 4,
  className = 'grid gap-4 md:grid-cols-2',
}) => (
  <div className={`relative ${className}`} aria-hidden="true">
    <RubyLoader variant="overlay" />
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <Skeleton variant="light" className="h-16 w-16 rounded-lg shrink-0" />
          <div className="flex-1 space-y-2.5 pt-1">
            <Skeleton variant="light" className="h-3.5 w-2/3" />
            <Skeleton variant="light" className="h-3 w-1/2" />
            <Skeleton variant="light" className="h-3 w-1/3" />
          </div>
        </div>
      </div>
    ))}
  </div>
);
